import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/esm/Add';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Delete from '@mui/icons-material/esm/Delete';
import Edit from '@mui/icons-material/esm/Edit';
import FactCheck from '@mui/icons-material/esm/FactCheck';
import PlaylistAdd from '@mui/icons-material/esm/PlaylistAdd';
import Refresh from '@mui/icons-material/esm/Refresh';
import Warning from '@mui/icons-material/esm/Warning';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import risksService, {
  MitigationStatus,
  RiskCategory,
  RiskHeatMapCell,
  RiskPayload,
  RiskRegisterRow,
  RiskStatus,
} from '../../services/api/risksService';

const CATEGORIES: RiskCategory[] = ['financial', 'operational', 'regulatory', 'safety', 'weather', 'other'];
const STATUSES: RiskStatus[] = ['open', 'mitigating', 'accepted', 'closed'];
const MITIGATION_STATUSES: MitigationStatus[] = ['planned', 'in_progress', 'done', 'cancelled'];
const CATEGORY_LABELS: Record<RiskCategory, string> = {
  financial: 'Financial',
  operational: 'Operational',
  regulatory: 'Regulatory',
  safety: 'Safety',
  weather: 'Weather',
  other: 'Other',
};

const emptyForm = (projectId: string | null): RiskPayload => ({
  projectId: projectId ?? '',
  riskCode: `RISK-${Date.now().toString().slice(-6)}`,
  title: '',
  description: '',
  category: 'operational',
  probability: 3,
  impact: 3,
  status: 'open',
});

const scoreColor = (score: number): 'success' | 'warning' | 'error' | 'info' =>
  score >= 15 ? 'error' : score >= 10 ? 'warning' : score >= 5 ? 'info' : 'success';

const cellColor = (score: number) => {
  if (score >= 15) return { bgcolor: 'error.main', color: 'error.contrastText' };
  if (score >= 10) return { bgcolor: 'warning.main', color: 'warning.contrastText' };
  if (score >= 5) return { bgcolor: '#fdd835', color: '#1f1f1f' };
  return { bgcolor: 'success.main', color: 'success.contrastText' };
};

export default function RiskRegisterPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [risks, setRisks] = useState<RiskRegisterRow[]>([]);
  const [topRisks, setTopRisks] = useState<RiskRegisterRow[]>([]);
  const [heatMap, setHeatMap] = useState<RiskHeatMapCell[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<{ probability: number; impact: number } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState<RiskRegisterRow | null>(null);
  const [riskForm, setRiskForm] = useState<RiskPayload>(emptyForm(null));
  const [planRisk, setPlanRisk] = useState<RiskRegisterRow | null>(null);
  const [planDescription, setPlanDescription] = useState('');
  const [planDueDate, setPlanDueDate] = useState('');
  const [planStatus, setPlanStatus] = useState<MitigationStatus>('planned');

  const fetchData = async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const [riskRows, mapRows, topRows] = await Promise.all([
        risksService.list({ projectId: activeProjectId }),
        risksService.heatMap(activeProjectId),
        risksService.top(activeProjectId, 10),
      ]);
      setRisks(riskRows);
      setHeatMap(mapRows);
      setTopRisks(topRows);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load risk register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  const filteredRisks = useMemo(() => {
    if (!selectedBucket) return risks;
    return risks.filter((risk) => risk.probability === selectedBucket.probability && risk.impact === selectedBucket.impact);
  }, [risks, selectedBucket]);

  const summary = useMemo(() => {
    const open = risks.filter((risk) => risk.status === 'open').length;
    const mitigating = risks.filter((risk) => risk.status === 'mitigating').length;
    const high = risks.filter((risk) => risk.status !== 'closed' && risk.riskScore >= 15).length;
    const duePlans = risks.reduce((count, risk) => (
      count + (risk.mitigationPlans ?? []).filter((plan) => plan.status !== 'done' && plan.status !== 'cancelled').length
    ), 0);
    return { total: risks.length, open, mitigating, high, duePlans };
  }, [risks]);

  const openCreate = () => {
    setEditingRisk(null);
    setRiskForm(emptyForm(activeProjectId));
    setFormOpen(true);
  };

  const openEdit = (risk: RiskRegisterRow) => {
    setEditingRisk(risk);
    setRiskForm({
      projectId: risk.projectId,
      riskCode: risk.riskCode,
      title: risk.title,
      description: risk.description ?? '',
      category: risk.category,
      probability: risk.probability,
      impact: risk.impact,
      status: risk.status,
      ownerUserId: risk.ownerUserId,
      residualProbability: risk.residualProbability,
      residualImpact: risk.residualImpact,
    });
    setFormOpen(true);
  };

  const saveRisk = async () => {
    try {
      if (!riskForm.projectId) {
        setError('Select a project before creating a risk.');
        return;
      }
      if (editingRisk) {
        await risksService.update(editingRisk.id, riskForm);
      } else {
        await risksService.create(riskForm);
      }
      setFormOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save risk');
    }
  };

  const closeRisk = async (risk: RiskRegisterRow) => {
    try {
      await risksService.close(risk.id);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close risk');
    }
  };

  const addPlan = async () => {
    if (!planRisk || !planDescription.trim()) return;
    try {
      await risksService.addMitigationPlan(planRisk.id, {
        description: planDescription,
        dueDate: planDueDate || null,
        status: planStatus,
      });
      setPlanDescription('');
      setPlanDueDate('');
      setPlanStatus('planned');
      const refreshed = await risksService.list({ projectId: activeProjectId ?? undefined });
      setRisks(refreshed);
      setPlanRisk(refreshed.find((risk) => risk.id === planRisk.id) ?? planRisk);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save mitigation plan');
    }
  };

  const updatePlan = async (planId: string, status: MitigationStatus) => {
    try {
      await risksService.updateMitigationPlan(planId, { status });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update mitigation plan');
    }
  };

  const deletePlan = async (planId: string) => {
    try {
      await risksService.deleteMitigationPlan(planId);
      await fetchData();
      if (planRisk) {
        setPlanRisk({
          ...planRisk,
          mitigationPlans: (planRisk.mitigationPlans ?? []).filter((plan) => plan.id !== planId),
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete mitigation plan');
    }
  };

  const columns: Column<RiskRegisterRow>[] = [
    { id: 'riskCode', label: 'Code', minWidth: 110 },
    { id: 'title', label: 'Risk', minWidth: 220 },
    {
      id: 'category',
      label: 'Category',
      minWidth: 110,
      format: (value: RiskCategory) => <Chip size="small" label={CATEGORY_LABELS[value] ?? value} />,
    },
    { id: 'probability', label: 'P', minWidth: 60, align: 'center' },
    { id: 'impact', label: 'I', minWidth: 60, align: 'center' },
    {
      id: 'riskScore',
      label: 'Score',
      minWidth: 90,
      align: 'center',
      format: (value: number) => <Chip size="small" color={scoreColor(value)} label={value} />,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value: RiskStatus) => <Chip size="small" label={value.replace('_', ' ')} color={value === 'closed' ? 'default' : value === 'mitigating' ? 'warning' : 'primary'} />,
    },
    {
      id: 'mitigationPlans',
      label: 'Plans',
      minWidth: 80,
      align: 'center',
      format: (_value, row) => row.mitigationPlans?.length ?? 0,
    },
    {
      id: 'actions',
      label: '',
      minWidth: 150,
      sortable: false,
      format: (_value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit risk">
            <IconButton size="small" onClick={() => openEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mitigation plans">
            <IconButton size="small" onClick={() => setPlanRisk(row)}>
              <PlaylistAdd fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status !== 'closed' && (
            <Tooltip title="Close risk">
              <IconButton size="small" onClick={() => closeRisk(row)}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Risk Register" />
        <Alert severity="info">Select a project to view the risk register.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Risk Register"
        subtitle={selectedBucket ? `Filtered to P${selectedBucket.probability} / I${selectedBucket.impact}` : `${summary.total} risks tracked`}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
              New Risk
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Open Risks" value={`${summary.open}`} icon={<Warning />} color={summary.open > 0 ? 'warning' : 'success'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Mitigating" value={`${summary.mitigating}`} icon={<FactCheck />} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="High Score" value={`${summary.high}`} icon={<Warning />} color={summary.high > 0 ? 'error' : 'success'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Open Actions" value={`${summary.duePlans}`} icon={<PlaylistAdd />} color={summary.duePlans > 0 ? 'primary' : 'success'} />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Heat Map
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '48px repeat(5, 1fr)', gap: 1, alignItems: 'stretch' }}>
              <Box />
              {[1, 2, 3, 4, 5].map((p) => (
                <Typography key={p} variant="caption" align="center">P{p}</Typography>
              ))}
              {[5, 4, 3, 2, 1].map((impact) => (
                <Fragment key={impact}>
                  <Typography key={`label-${impact}`} variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>I{impact}</Typography>
                  {heatMap
                    .filter((cell) => cell.impact === impact)
                    .sort((a, b) => a.probability - b.probability)
                    .map((cell) => (
                      <Button
                        key={`${cell.probability}-${cell.impact}`}
                        onClick={() => setSelectedBucket(
                          selectedBucket?.probability === cell.probability && selectedBucket?.impact === cell.impact
                            ? null
                            : { probability: cell.probability, impact: cell.impact }
                        )}
                        sx={{
                          minWidth: 0,
                          aspectRatio: '1 / 1',
                          borderRadius: 1,
                          ...cellColor(cell.riskScore),
                          outline: selectedBucket?.probability === cell.probability && selectedBucket?.impact === cell.impact
                            ? '3px solid'
                            : 'none',
                          outlineColor: 'text.primary',
                        }}
                      >
                        {cell.count}
                      </Button>
                    ))}
                </Fragment>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Top 10 Risks
            </Typography>
            <DataTable columns={columns.filter((column) => column.id !== 'actions')} data={topRisks} pagination={false} searchable={false} />
          </Box>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable columns={columns} data={filteredRisks} searchable pagination defaultRowsPerPage={10} />
        </Box>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRisk ? 'Edit Risk' : 'New Risk'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, pt: 2 }}>
          <TextField label="Code" value={riskForm.riskCode} onChange={(e) => setRiskForm({ ...riskForm, riskCode: e.target.value })} />
          <TextField label="Title" value={riskForm.title} onChange={(e) => setRiskForm({ ...riskForm, title: e.target.value })} />
          <TextField select label="Category" value={riskForm.category} onChange={(e) => setRiskForm({ ...riskForm, category: e.target.value as RiskCategory })}>
            {CATEGORIES.map((category) => <MenuItem key={category} value={category}>{CATEGORY_LABELS[category]}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={riskForm.status ?? 'open'} onChange={(e) => setRiskForm({ ...riskForm, status: e.target.value as RiskStatus })}>
            {STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
          </TextField>
          <TextField type="number" label="Probability" inputProps={{ min: 1, max: 5 }} value={riskForm.probability} onChange={(e) => setRiskForm({ ...riskForm, probability: Number(e.target.value) })} />
          <TextField type="number" label="Impact" inputProps={{ min: 1, max: 5 }} value={riskForm.impact} onChange={(e) => setRiskForm({ ...riskForm, impact: Number(e.target.value) })} />
          <TextField type="number" label="Residual Probability" inputProps={{ min: 1, max: 5 }} value={riskForm.residualProbability ?? ''} onChange={(e) => setRiskForm({ ...riskForm, residualProbability: e.target.value ? Number(e.target.value) : null })} />
          <TextField type="number" label="Residual Impact" inputProps={{ min: 1, max: 5 }} value={riskForm.residualImpact ?? ''} onChange={(e) => setRiskForm({ ...riskForm, residualImpact: e.target.value ? Number(e.target.value) : null })} />
          <TextField
            label="Description"
            value={riskForm.description ?? ''}
            onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })}
            multiline
            minRows={3}
            sx={{ gridColumn: { md: '1 / span 2' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRisk}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(planRisk)} onClose={() => setPlanRisk(null)} maxWidth="md" fullWidth>
        <DialogTitle>Mitigation Plans</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {planRisk && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{planRisk.riskCode} - {planRisk.title}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 160px 160px auto' }, gap: 1.5, mb: 2 }}>
                <TextField label="Action" value={planDescription} onChange={(e) => setPlanDescription(e.target.value)} />
                <TextField type="date" label="Due" InputLabelProps={{ shrink: true }} value={planDueDate} onChange={(e) => setPlanDueDate(e.target.value)} />
                <TextField select label="Status" value={planStatus} onChange={(e) => setPlanStatus(e.target.value as MitigationStatus)}>
                  {MITIGATION_STATUSES.map((status) => <MenuItem key={status} value={status}>{status.replace('_', ' ')}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={addPlan} startIcon={<Add />}>Add</Button>
              </Box>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {(planRisk.mitigationPlans ?? []).map((plan) => (
                <Box key={plan.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 140px 160px 44px' }, gap: 1, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2">{plan.description}</Typography>
                  <Typography variant="body2" color="text.secondary">{plan.dueDate ?? 'No due date'}</Typography>
                  <TextField select size="small" value={plan.status} onChange={(e) => updatePlan(plan.id, e.target.value as MitigationStatus)}>
                    {MITIGATION_STATUSES.map((status) => <MenuItem key={status} value={status}>{status.replace('_', ' ')}</MenuItem>)}
                  </TextField>
                  <Tooltip title="Delete plan">
                    <IconButton size="small" onClick={() => deletePlan(plan.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                ))}
                {(planRisk.mitigationPlans ?? []).length === 0 && <Alert severity="info">No mitigation plans recorded.</Alert>}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanRisk(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
